import React, { useState } from 'react';
import { trackContactFormSubmit, trackEvent } from '../utils/analytics';

function ContactSection({ language, isVisible = false }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '', // Honeypot field for spam protection
  });
  
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
    success: false
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [submitAttempts, setSubmitAttempts] = useState(0);
  
  const translations = {
    title: {
      en: 'Contact Me',
      de: 'Kontaktieren Sie Mich'
    },
    getInTouch: {
      title: { 
        en: 'Get In Touch', 
        de: 'Nehmen Sie Kontakt auf' 
      },
      text: {
        en: 'Interested in my artwork or have a collaboration in mind? Feel free to reach out through the contact form.',
        de: 'Interessieren Sie sich für meine Kunst oder haben Sie eine Zusammenarbeit im Sinn? Kontaktieren Sie mich gerne über das Kontaktformular.'
      }
    },
    commission: {
      title: { 
        en: 'Commissioning Artwork', 
        de: 'Kunstwerke in Auftrag geben' 
      },
      text: {
        en: 'I am available for commissions. Please provide your contact details in your inquiry so we can discuss the details personally.',
        de: 'Ich nehme gerne Aufträge entgegen. Bitte übermitteln Sie mir bei Ihrer Anfrage Ihre Kontaktdaten, damit wir die Einzelheiten persönlich besprechen können.'
      }
    },
    form: {
      title: { 
        en: 'Send Me a Message', 
        de: 'Senden Sie mir eine Nachricht' 
      },
      name: { 
        en: 'Name', 
        de: 'Name' 
      },
      email: { 
        en: 'Email', 
        de: 'E-Mail' 
      },
      subject: { 
        en: 'Subject', 
        de: 'Betreff' 
      },
      message: { 
        en: 'Message', 
        de: 'Nachricht' 
      },
      button: { 
        en: 'Send Message', 
        de: 'Nachricht senden' 
      },
      submitting: { 
        en: 'Sending...', 
        de: 'Wird gesendet...' 
      },
      success: { 
        en: 'Thank you! Your message has been sent.', 
        de: 'Vielen Dank! Ihre Nachricht wurde gesendet.' 
      },
      error: { 
        en: 'Sorry, there was a problem sending your message.', 
        de: 'Entschuldigung, beim Senden Ihrer Nachricht ist ein Problem aufgetreten.' 
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear validation error when user starts typing
    if (validationErrors[e.target.name]) {
      setValidationErrors({
        ...validationErrors,
        [e.target.name]: null
      });
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    // Basic email regex pattern
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return false;
    }
    
    // Check for common invalid patterns
    if (email.endsWith('.') || email.indexOf('..') !== -1) {
      return false;
    }
    
    return true;
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = language === 'en' ? 'Please enter your name' : 'Bitte geben Sie Ihren Namen ein';
    }
    
    if (!formData.email.trim()) {
      errors.email = language === 'en' ? 'Please enter your email' : 'Bitte geben Sie Ihre E-Mail-Adresse ein';
    } else if (!validateEmail(formData.email)) {
      errors.email = language === 'en' ? 'Please enter a valid email address' : 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = language === 'en' ? 'Please enter a subject' : 'Bitte geben Sie einen Betreff ein';
    }
    
    if (!formData.message.trim()) {
      errors.message = language === 'en' ? 'Please enter a message' : 'Bitte geben Sie eine Nachricht ein';
    } else if (formData.message.trim().length < 10) {
      errors.message = language === 'en' ? 'Message is too short (min 10 characters)' : 'Die Nachricht ist zu kurz (min. 10 Zeichen)';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for multiple rapid submissions (spam protection)
    setSubmitAttempts(prev => prev + 1);
    if (submitAttempts >= 3) {
      setStatus({
        submitting: false,
        submitted: true,
        error: language === 'en' ? 
          'Too many submissions. Please try again later.' : 
          'Zu viele Einreichungen. Bitte versuchen Sie es später erneut.',
        success: false
      });
      return;
    }
    
    // Honeypot check (spam protection)
    if (formData.honeypot) {
      console.log('Bot detected via honeypot field');
      // Pretend success but don't actually submit
      setStatus({
        submitting: false,
        submitted: true,
        error: null,
        success: true
      });
      return;
    }
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setStatus({ ...status, submitting: true });
    
    try {
      // Use relative URL - nginx proxies /api to backend
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });
      
      if (response.ok) {
        // Track successful contact form submission
        trackContactFormSubmit();
        
        setStatus({
          submitting: false,
          submitted: true,
          error: null,
          success: true
        });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          honeypot: ''
        });
      } else {
        // Track failed submission
        trackEvent('contact_form_error', { reason: 'server_error' });
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({
        submitting: false,
        submitted: true,
        error: language === 'en' ? 
          'There was an error sending your message. Please try again later.' : 
          'Beim Senden Ihrer Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
        success: false
      });
    }
  };

  return (
    <section id="contact" className={`section-content ${!isVisible ? 'hidden' : ''}`}>
      <div className="container">
        <h1>{translations.title[language]}</h1>
        <div className="contact-container">
          <div className="contact-info">
            <h2>{translations.getInTouch.title[language]}</h2>
            <p>{translations.getInTouch.text[language]}</p>
            
            <h2>{translations.commission.title[language]}</h2>
            <p>{translations.commission.text[language]}</p>
          </div>
          
          <div className="contact-form">
            <h2>{translations.form.title[language]}</h2>
            
            {status.submitted && status.success && (
              <div className="form-status success">{translations.form.success[language]}</div>
            )}
            
            {status.submitted && !status.success && status.error && (
              <div className="form-status error">{status.error}</div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">{translations.form.name[language]}</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  className={validationErrors.name ? 'error' : ''}
                />
                {validationErrors.name && <span className="error-message">{validationErrors.name}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="email">{translations.form.email[language]}</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  className={validationErrors.email ? 'error' : ''}
                />
                {validationErrors.email && <span className="error-message">{validationErrors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="subject">{translations.form.subject[language]}</label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  value={formData.subject}
                  onChange={handleChange}
                  className={validationErrors.subject ? 'error' : ''}
                />
                {validationErrors.subject && <span className="error-message">{validationErrors.subject}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="message">{translations.form.message[language]}</label>
                <textarea 
                  id="message" 
                  name="message" 
                  value={formData.message}
                  onChange={handleChange}
                  className={validationErrors.message ? 'error' : ''}
                ></textarea>
                {validationErrors.message && <span className="error-message">{validationErrors.message}</span>}
              </div>
              
              {/* Honeypot field - invisible to humans but bots will fill it */}
              <div className="honeypot-field" aria-hidden="true">
                <label htmlFor="honeypot">Leave this field empty</label>
                <input 
                  type="text" 
                  id="honeypot" 
                  name="honeypot" 
                  value={formData.honeypot}
                  onChange={handleChange}
                  tabIndex="-1"
                />
              </div>
              
              <button 
                type="submit" 
                disabled={status.submitting}
              >
                {status.submitting ? translations.form.submitting[language] : translations.form.button[language]}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactSection;
